import type { PaginationState, RowData, Table } from "@tanstack/react-table";
import {
	ArrowLeft,
	ArrowRight,
	ChevronLeft,
	ChevronRight,
} from "@untitledui/icons";
import { IconButton } from "@/components/Atoms/IconButton/IconButton";
import { Button } from "@/components/base/buttons/button";
import { Select } from "@/components/base/select/select";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useBreakpoint } from "@/hooks/use-breakpoint";
import { cx } from "@/utils/cx";
import { Pagination } from "../Pagination/PaginationBase";
import type { AppTableFeatures } from "./app-table-features";

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

export interface TablePaginationProps {
	/** Zero-based page index (TanStack Table). */
	pageIndex: number;
	/** Rows per page. */
	pageSize: number;
	/** Total number of pages. */
	pageCount: number;
	/** Called with the next TanStack pagination state. */
	onPaginationChange: (next: PaginationState) => void;
	/**
	 * Total number of rows across all pages. Used for the Web
	 * "Showing X-Y of Z" label. When omitted, derived from
	 * `pageCount * pageSize` (approximate on the last page).
	 */
	totalRowCount?: number;
	/** Available page-size options for the Web "Rows per page" dropdown. */
	pageSizeOptions?: number[];
	/**
	 * Force Web or Mobile layout. When omitted, uses the `md` breakpoint
	 * (`md` and up = web).
	 */
	device?: "web" | "mobile";
	/** Extra classes on the root nav. */
	className?: string;
	/** Sibling page count for ellipsis windows. */
	siblingCount?: number;
	/**
	 * When set, the Web summary is "Showing N of M · K selected"
	 * instead of the range label. Omit it to keep "Showing X-Y of Z".
	 */
	selectedCount?: number;
}

/**
 * Maps a TanStack Table instance to controlled {@link TablePagination} props.
 *
 * @param table - TanStack table instance.
 * @param options - Optional override for `pageCount` / `totalRowCount` (e.g. server-side).
 */
export function tablePaginationProps<TData extends RowData = RowData>(
	table: Table<AppTableFeatures, TData>,
	options?: { pageCount?: number; totalRowCount?: number },
): Pick<
	TablePaginationProps,
	| "pageIndex"
	| "pageSize"
	| "pageCount"
	| "totalRowCount"
	| "onPaginationChange"
> {
	const { pageIndex, pageSize } = table.store.state.pagination;

	return {
		pageIndex,
		pageSize,
		pageCount: options?.pageCount ?? table.getPageCount(),
		totalRowCount: options?.totalRowCount,
		onPaginationChange: (next) => {
			table.setPagination(next);
		},
	};
}

/**
 * Table pagination control matching Figma TablePagination (3115:11018).
 *
 * Web: "Showing X-Y of Z", icon prev/next + numbered pages, "Rows per page" select.
 * Mobile: Previous / Next with "Page X of Y".
 */
export function TablePagination({
	pageIndex,
	pageSize,
	pageCount,
	onPaginationChange,
	totalRowCount,
	pageSizeOptions = [...DEFAULT_PAGE_SIZE_OPTIONS],
	device,
	className,
	siblingCount = 1,
	selectedCount,
}: TablePaginationProps) {
	const { t } = useAppTranslation();
	const isMdUp = useBreakpoint("md");
	const isWeb = device ? device === "web" : isMdUp;

	const currentPage = pageIndex + 1;
	const total = Math.max(0, pageCount);
	const canPreviousPage = pageIndex > 0 && total > 0;
	const canNextPage = pageIndex < total - 1 && total > 0;

	const resolvedTotalRows = totalRowCount ?? (total > 0 ? total * pageSize : 0);
	const rangeStart = resolvedTotalRows === 0 ? 0 : pageIndex * pageSize + 1;
	const rangeEnd =
		resolvedTotalRows === 0
			? 0
			: Math.min(pageIndex * pageSize + pageSize, resolvedTotalRows);

	const pageSizeItems = pageSizeOptions.map((size) => ({
		id: String(size),
		label: String(size),
	}));

	const pageSizeKey =
		pageSizeItems.find((item) => Number(item.label) === pageSize)?.id ??
		pageSizeItems[0]?.id;

	const goToPage = (pageOneBased: number) => {
		const nextIndex = Math.max(0, Math.min(pageOneBased - 1, total - 1));
		if (nextIndex === pageIndex) return;
		onPaginationChange({ pageIndex: nextIndex, pageSize });
	};

	const handlePageSizeChange = (value: string | number | null) => {
		if (value == null) return;
		const newSize = Number(
			pageSizeItems.find((item) => item.id === String(value))?.label ??
				pageSize,
		);
		if (!Number.isFinite(newSize) || newSize === pageSize) return;
		onPaginationChange({ pageIndex: 0, pageSize: newSize });
	};

	if (!isWeb) {
		return (
			<nav
				aria-label={t("common.pagination.page")}
				className={cx(
					// Figma spacing-md (8px) top + bottom
					"flex w-full items-center justify-between gap-3 py-(--spacing-md,8px)",
					className,
				)}
			>
				<Button
					aria-label={t("common.pagination.previous")}
					iconLeading={ArrowLeft}
					color="secondary"
					size="sm"
					isDisabled={!canPreviousPage}
					onClick={() => goToPage(currentPage - 1)}
				>
					{t("common.pagination.previous")}
				</Button>

				<span className="text-sm font-medium whitespace-pre text-primary">
					{t("common.pagination.page")}{" "}
					<span className="font-medium">{total > 0 ? currentPage : 0}</span>{" "}
					{t("common.pagination.of")}{" "}
					<span className="font-medium">{total}</span>
				</span>

				<Button
					aria-label={t("common.pagination.next")}
					iconTrailing={ArrowRight}
					color="secondary"
					size="sm"
					isDisabled={!canNextPage}
					onClick={() => goToPage(currentPage + 1)}
				>
					{t("common.pagination.next")}
				</Button>
			</nav>
		);
	}

	return (
		<Pagination.Root
			page={total > 0 ? currentPage : 1}
			total={total > 0 ? total : 1}
			siblingCount={siblingCount}
			onPageChange={goToPage}
			className={cx(
				// Figma TablePagination (3115:11018) — spacing-md (8px) top + bottom
				"flex w-full items-center justify-between gap-3 py-(--spacing-md,8px)",
				className,
			)}
		>
			{/* Results text — "Showing 1-25 of 250", or "Showing N of M · K selected" */}
			<div className="flex shrink-0 items-center gap-3">
				<p className="text-sm font-medium text-primary">
					{selectedCount == null
						? `${t("common.pagination.showing")} ${rangeStart}-${rangeEnd} ${t("common.pagination.of")} ${resolvedTotalRows}`
						: `${t("common.pagination.showingRowsOfTotal", {
								rowCount:
									resolvedTotalRows === 0 ? 0 : rangeEnd - rangeStart + 1,
								totalRowCount: resolvedTotalRows,
							})} · ${t("common.selectInput.multiSummary", {
								count: selectedCount,
							})}`}
				</p>
			</div>

			{/* Page controls — icon prev/next + numbered pages */}
			<div className="flex min-w-0 items-center gap-2">
				<Pagination.PrevTrigger asChild>
					<IconButton
						icon={ChevronLeft}
						hierarchy="secondary"
						size="sm"
						shape="square"
						aria-label={t("common.pagination.previous")}
						tooltip={t("common.pagination.previous")}
						isDisabled={!canPreviousPage}
					/>
				</Pagination.PrevTrigger>

				{total > 0 && (
					<Pagination.Context>
						{({ pages }) => (
							<div className="flex items-center gap-2">
								{pages.map((page) =>
									page.type === "page" ? (
										<Pagination.Item
											key={page.value}
											value={page.value}
											isCurrent={page.isCurrent}
											asChild
										>
											<Button
												color="secondary"
												size="sm"
												aria-label={`${t("common.pagination.page")} ${page.value}`}
												aria-current={page.isCurrent ? "page" : undefined}
												className={cx(
													"min-w-10",
													page.isCurrent && "bg-tertiary",
												)}
											>
												{page.value}
											</Button>
										</Pagination.Item>
									) : (
										<Pagination.Ellipsis key={page.key}>
											<Button
												color="secondary"
												size="sm"
												isDisabled
												aria-hidden
												className="pointer-events-none min-w-10"
											>
												&#8230;
											</Button>
										</Pagination.Ellipsis>
									),
								)}
							</div>
						)}
					</Pagination.Context>
				)}

				<Pagination.NextTrigger asChild>
					<IconButton
						icon={ChevronRight}
						hierarchy="secondary"
						size="sm"
						shape="square"
						aria-label={t("common.pagination.next")}
						tooltip={t("common.pagination.next")}
						isDisabled={!canNextPage}
					/>
				</Pagination.NextTrigger>
			</div>

			{/* Rows per page selector */}
			<div className="flex shrink-0 items-center justify-end gap-1">
				<span className="text-sm font-medium text-primary">
					{t("common.pagination.rowsPerPage")}
				</span>
				{pageSizeKey && (
					<Select
						data-testid="table-pagination-size-select"
						aria-label={t("common.pagination.rowsPerPage")}
						size="sm"
						items={pageSizeItems}
						value={pageSizeKey}
						className="w-fit"
						onChange={handlePageSizeChange}
					>
						{(option) => (
							<Select.Item
								id={option.id}
								data-testid={`table-pagination-size-option-${option.id}`}
								className="[&_svg:not([data-icon])]:hidden"
							>
								{option.label}
							</Select.Item>
						)}
					</Select>
				)}
			</div>
		</Pagination.Root>
	);
}
